import clsx from 'clsx';
import { isEmpty } from 'lodash-es';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';
import {
  EventAccordionItemProps,
  translateEventToInventoryAccordionItem,
} from 'src/components/Accordions';
import { StubHubListingManualPricingContextProvider } from 'src/components/Listings/InventoryEventPage/MarketListings/StubHubListingsManualPricingContext';
import { ActiveFocusContextProvider } from 'src/contexts/ActiveFocusContext/ActiveFocusContext';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { ActiveSortTableColumnProvider } from 'src/contexts/ActiveSortTableColumnContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useEventHubContext } from 'src/contexts/EventHubContext/EventHubContext';
import { EventMapContextProvider } from 'src/contexts/EventMapContext';
import { InputPriceFocusProvider } from 'src/contexts/InputPriceFocusContext/InputPriceFocusContext';
import { MobileDrawerProvider } from 'src/contexts/MobileDrawerContext';
import { MobileListingDrawerContextWrapper } from 'src/contexts/MobileListingDrawerContext/MobileListingDrawerContext';
import {
  IneventorySortingType,
  MobileInventorySortAndFilterContextProvider,
} from 'src/contexts/MobileSortAndFilterContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import {
  EventLastReviewedContextProvider,
  useEventLastReviewedContext,
} from 'src/contexts/SellerEventLastReviewContext/SellerEventLastReviewContext';
import {
  SidePanelPage,
  SidePanelProvider,
} from 'src/contexts/SidePanelContext';
import { ExplorerLayout } from 'src/core/layouts';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { globalMobileWrapper } from 'src/core/themes/themeContract.css';
import { Stack } from 'src/core/ui';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { InventoryDeeplinkQueryParam } from 'src/utils/constants/constants';
import { DefaultListingTableSorting } from 'src/utils/constants/tableColumns';
import { getDeepLinkIdFromUrl } from 'src/utils/deepLinkUtils';
import { getListingDetailsModalConfigWithDeepLink } from 'src/utils/inventoryUtils';
import {
  ActionOutboxEntityType,
  EventWithData,
  Feature,
  Listing,
  ListingDetailDataField,
  ListingDetails,
} from 'src/WebApiController';
import Swiper from 'swiper';

import { InventoryEventCatchUpBar } from '../InventoryEventCatchUp';
import { InventoryEventPageRightPanel } from '../InventoryEventPageRightPanel';
import { InventoryEventHeader } from './EventHeader/InventoryEventHeader';
import {
  InventoryEventLocalContextProvider,
  useInventoryEventLocalContext,
} from './InventoryEventLocalContext';
import * as styles from './InventoryEventPage.css';
import { InventoryEventPageContent } from './InventoryEventPageContent';
import { InventoryEventContentMobile } from './Mobile/InventoryEventContentMobile';

export function InventoryEventPageRework({
  viagVirtId,
  posEventIds,
  returnUrl,
}: {
  posEventIds?: string[];
  viagVirtId?: string;
  returnUrl?: string;
}) {
  const { activeAccountWebClientConfig } = useAppContext();
  const hasPricingCatchupFeature = useUserHasFeature(Feature.PricingCatchup);
  const hasMobileInventoryPageFeature = useUserHasFeature(
    Feature.MobileInventoryEventPage
  );
  const isMobileLayout =
    useMatchMedia('mobile') && hasMobileInventoryPageFeature;
  const {
    eventsTransformed,
    eventsExpansion: { setListItemExpansion },
  } = useCatalogDataContext();
  const { setSelectionMode } = useMultiSelectionContext();

  useEffect(() => {
    setSelectionMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viagVirtId]);

  useEffect(() => {
    if (!viagVirtId && posEventIds?.length) {
      setListItemExpansion(
        true,
        posEventIds.map((id) => `old:${id}`)
      );
    } else if (viagVirtId) {
      setListItemExpansion(true, [viagVirtId]);
    }
  }, [
    activeAccountWebClientConfig,
    posEventIds,
    setListItemExpansion,
    viagVirtId,
  ]);

  const { allGroupsIds, setAllGroupsIds } = useMultiSelectionContext();

  const accordionItemProps = useMemo(
    () =>
      eventsTransformed?.length
        ? eventsTransformed.find(
            (ev) =>
              ev.event.virtId === viagVirtId ||
              ev.event.posIds.some((id) => posEventIds?.includes(id))
          )
        : undefined,
    [eventsTransformed, posEventIds, viagVirtId]
  );

  const inventoryAccordionItem = useMemo(
    () =>
      accordionItemProps &&
      translateEventToInventoryAccordionItem(accordionItemProps, 0, true),
    [accordionItemProps]
  );

  useEffect(() => {
    // Important useEffect to keep allGroupIds in sync with the items
    // This is important for the multi select to work properly
    if (
      inventoryAccordionItem &&
      !isEqual(allGroupsIds, [inventoryAccordionItem.itemId])
    ) {
      setAllGroupsIds([inventoryAccordionItem.itemId]);
    }
  }, [allGroupsIds, inventoryAccordionItem, setAllGroupsIds]);

  return (
    <MobileInventorySortAndFilterContextProvider
      type={IneventorySortingType.Listing}
    >
      <MobileDrawerProvider>
        <MobileListingDrawerContextWrapper>
          <SidePanelProvider sidePanelId="inventory-event">
            <ActiveFocusContextProvider>
              <EventMapContextProvider event={accordionItemProps?.event}>
                <InputPriceFocusProvider
                  disablePagination
                  pageSize={accordionItemProps?.counts?.listCnt ?? 0}
                >
                  <StubHubListingManualPricingContextProvider
                    groupId={accordionItemProps?.event.virtId}
                  >
                    <EventLastReviewedContextProvider>
                      <InventoryEventLocalContextProvider
                        accordionItemProps={accordionItemProps}
                      >
                        <Stack
                          direction="column"
                          height="full"
                          style={{ overflow: 'auto' }}
                          className={clsx({
                            [globalMobileWrapper]: isMobileLayout,
                          })}
                        >
                          <Stack
                            direction="column"
                            style={{
                              flex: 1,
                              minHeight: 0,
                              overflow: 'auto',
                            }}
                          >
                            {isMobileLayout ? (
                              <InventoryEventContentMobile
                                accordionItemProps={accordionItemProps}
                                inventoryAccordionItem={inventoryAccordionItem}
                                returnUrl={returnUrl}
                              />
                            ) : (
                              <InventoryEventContent
                                accordionItemProps={accordionItemProps}
                                inventoryAccordionItem={inventoryAccordionItem}
                                returnUrl={returnUrl}
                              />
                            )}
                          </Stack>
                          {hasPricingCatchupFeature && (
                            <InventoryEventCatchUpBar />
                          )}
                        </Stack>
                      </InventoryEventLocalContextProvider>
                    </EventLastReviewedContextProvider>
                  </StubHubListingManualPricingContextProvider>
                </InputPriceFocusProvider>
              </EventMapContextProvider>
            </ActiveFocusContextProvider>
          </SidePanelProvider>
        </MobileListingDrawerContextWrapper>
      </MobileDrawerProvider>
    </MobileInventorySortAndFilterContextProvider>
  );
}

type InventoryEventContentProps = {
  accordionItemProps?: EventWithData;
  inventoryAccordionItem?: EventAccordionItemProps<Listing>;
  returnUrl?: string;
};

function InventoryEventContent({
  accordionItemProps,
  inventoryAccordionItem,
  returnUrl,
}: InventoryEventContentProps) {
  const { isLoading } = useCatalogDataContext();
  const [selectedListingIds, setSelectedListingIds] = useState<number[]>([]);

  const { setModal } = useContext(ModalContext);
  const { posEntity: listing, setActivePosEntity } =
    useActivePosEntityContext<ListingDetails>();

  const activeId = getDeepLinkIdFromUrl(
    InventoryDeeplinkQueryParam,
    window.location.href
  );

  const isMobile = useMatchMedia('mobile');

  useEffect(() => {
    if (activeId?.length && !listing) {
      setActivePosEntity(parseInt(activeId), undefined, true, [
        ListingDetailDataField.Basic,
      ]);
      setModal(getListingDetailsModalConfigWithDeepLink(isMobile, activeId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const swiperRef = useRef<Swiper>();

  const event = accordionItemProps?.event;

  const { onSettingVirtualIds } = useEventLastReviewedContext();

  useEffect(() => {
    onSettingVirtualIds(event?.virtId ? [event?.virtId] : []);
  }, [event?.virtId, onSettingVirtualIds]);

  const {
    onSelectListings,
    selectedListingIds: companionModeSelectedListingIds,
    isOnSidePanel,
    enabled,
    sessionRole,
  } = useEventHubContext();

  useEffect(() => {
    if (isOnSidePanel && companionModeSelectedListingIds) {
      if (!isEqual(selectedListingIds, companionModeSelectedListingIds)) {
        setSelectedListingIds(companionModeSelectedListingIds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companionModeSelectedListingIds, isOnSidePanel]);

  useEffect(() => {
    // do nothing when not in companion mode
    if (!(enabled && sessionRole && !isOnSidePanel)) {
      return;
    }

    if (isEmpty(selectedListingIds)) {
      return;
    }

    onSelectListings(selectedListingIds);
  }, [
    enabled,
    isOnSidePanel,
    onSelectListings,
    selectedListingIds,
    sessionRole,
  ]);

  const { activeTab } = useInventoryEventLocalContext();

  if (isLoading || !event) {
    return <PosSpinner />;
  }

  return (
    <ActiveSortTableColumnProvider
      entityType={ActionOutboxEntityType.Listing}
      defaultSorting={DefaultListingTableSorting}
    >
      <div className={styles.root}>
        <ExplorerLayout
          swiperRef={swiperRef}
          defaultRatio={0.35}
          maxPanelPercent={0.5}
          minPanelSize={400}
          showExpandButton
          sidePanelPage={SidePanelPage.InventoryEvent}
          rightPanelContent={
            !(enabled && sessionRole) && (
              <InventoryEventPageRightPanel
                event={event}
                listings={accordionItemProps.entities.listings}
                selectedListingIds={selectedListingIds}
                activeTab={activeTab}
              />
            )
          }
        >
          <Stack direction="column" className={styles.leftContainer} gap="l">
            <InventoryEventHeader
              event={event}
              inventoryAccordionItem={inventoryAccordionItem}
              returnUrl={returnUrl}
            />
            {isOnSidePanel ? (
              <InventoryEventPageRightPanel
                event={event}
                listings={accordionItemProps?.entities?.listings}
                selectedListingIds={selectedListingIds}
                activeTab={activeTab}
              />
            ) : (
              <InventoryEventPageContent
                accordionItemProps={accordionItemProps}
                inventoryAccordionItem={inventoryAccordionItem}
                selectedListingIds={selectedListingIds}
                setSelectedListingIds={setSelectedListingIds}
                activeTab={activeTab}
              />
            )}
          </Stack>
        </ExplorerLayout>
      </div>
    </ActiveSortTableColumnProvider>
  );
}
